import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ArrowRightStartOnRectangleIcon, Bars3Icon } from '@heroicons/react/24/solid'
import clsx from 'clsx'

import BaseLink from 'src/components/links/BaseLink'
import { AppDispatch } from 'src/store'
import { logoutAction } from 'src/store/auth'
import { BaseDivProps } from 'src/types'

/** Dark background + blue accent flat button */
function BlueButton({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }) {
  return (
    <button
      className={[
        'inline-flex items-center justify-center rounded-lg border border-[#4E8FC6]/60',
        'bg-[#1a1a1a] px-3 py-2 text-sm font-semibold text-[#4E8FC6]',
        'hover:border-[#4E8FC6] hover:bg-[#222] active:bg-[#2a2a2a] transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4E8FC6]/30',
        className,
      ].join(' ')}
      {...props}
    >
      {children}
    </button>
  )
}

/** Small icon button with same theme */
function BlueIconButton({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }) {
  return (
    <button
      className={clsx(
        'inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#4E8FC6]/40',
        'bg-[#1a1a1a] text-[#4E8FC6] hover:text-[#6EB0E6] hover:border-[#4E8FC6]',
        'active:bg-[#222] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4E8FC6]/30',
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}

function Header({ className }: BaseDivProps) {
  const dispatch = useDispatch<AppDispatch>()
  const [openMenu, setOpenMenu] = useState(false)

  const logoutHandler = useCallback(() => {
    dispatch(logoutAction())
  }, [dispatch])

  const handleToggleMenu = useCallback(() => setOpenMenu((prev) => !prev), [])
  const handleCloseMenu = useCallback(() => setOpenMenu(false), [])

  return (
    <div className={clsx('sticky top-0 z-10 border-b border-white/10 bg-[#0b0c0f]/80 backdrop-blur', className)}>
      <div className="flex w-full items-center justify-between gap-8 px-3 py-3 md:px-6">
        {/* Brand */}
        <div className="flex items-center gap-3">
        <img
  className="h-[40px] w-[40px]"
  src="/monit_logo.png"
  alt="logo"
  style={{
    filter: 'grayscale(100%) brightness(130%) contrast(120%) drop-shadow(0 0 4px #4E8FC6)',
  }}
/>         <div className="text-2xl font-bold tracking-widest text-white/90">MONIT</div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden flex-auto items-center gap-6 sm:flex">
          <BaseLink to="/" className="text-[#4E8FC6] :text-white/80 hover:text-[#4E8FC6]">Home</BaseLink>
          <BaseLink to="/settings" className="text-[#4E8FC6] :text-white/80 hover:text-[#4E8FC6]">Settings</BaseLink>
          <BaseLink to="/trade-history" className="text-[#4E8FC6] :text-white/80 hover:text-[#4E8FC6]">Utilities</BaseLink>
        </nav>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2 sm:flex">
          <BlueButton onClick={logoutHandler}>
            <ArrowRightStartOnRectangleIcon className="mr-1 h-5 w-5" />
            Logout
          </BlueButton>
        </div>

        {/* Mobile menu button */}
        <div className="sm:hidden">
          <BlueIconButton onClick={handleToggleMenu} aria-label="Toggle menu">
            <Bars3Icon className="h-5 w-5" />
          </BlueIconButton>
        </div>
      </div>

      {/* Mobile overlay menu */}
      <div
        className={clsx(
          'fixed bottom-0 left-0 right-0 top-[58px] hidden flex-col bg-black/80 sm:!hidden',
          { '!flex': openMenu }
        )}
        onClick={handleCloseMenu}
      >
        <div className="absolute left-0 right-0 top-0 z-10 flex flex-col gap-1 border-b border-white/10 bg-[#0b0c0f]/95 p-2">
          <div className="hover:bg-white/5">
            <BaseLink className="block px-4 py-3 text-[#4E8FC6] :text-white/90" to="/">Home</BaseLink>
          </div>
          <div className="hover:bg-white/5">
            <BaseLink className="block px-4 py-3 text-[#4E8FC6] :text-white/90" to="/settings">Settings</BaseLink>
          </div>
          <div className="hover:bg-white/5">
            <BaseLink className="block px-4 py-3 text-[#4E8FC6] :text-white/90" to="/trade-history">Utilities</BaseLink>
          </div>
          <div className="flex justify-start px-2 py-2">
            <BlueButton onClick={logoutHandler}>Logout</BlueButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Header
